import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, CalendarDays, Check, ChevronRight, CircleSlash, FileText, Plus, Undo2, Wallet } from "lucide-react";
import { apiError, recruiterSelfApi } from "../api";
import { dayLong, lineTitle, money, pct, PRODUCER_STATUS, relative, STAGE, STATEMENT_STATUS } from "../format";
import { Badge, CommissionBreakdownView, CommissionStatusBadge, Empty, KeyValue, SectionTitle, Stat, Tabs, type TabDef } from "../ui";
import { EventCard } from "../EventCard";
import type { ChecklistItem, MyWorkspace, RecruiterEventRow, Todo } from "../types";
import { ActivityModal, ForceMajeureModal, InvoiceModal, PaymentForm, ProposeModal, ReportModal, SimultaneousModal } from "./selfModals";
import { LoadingBlock, ErrorBlock } from "../../dashboard/shared/StateBlocks";
import { TermsCheck } from "./TermsCheck";
import { useToast } from "@/app/providers/Toaster";

type Tab = "inicio" | "cartera" | "eventos" | "ganancias" | "datos";
const TABS: Tab[] = ["inicio", "cartera", "eventos", "ganancias", "datos"];

/**
 * "Mi trabajo": el espacio del reclutador dentro de Nexus. Sólo ve lo suyo
 * (el backend filtra por quien entró). Pestañas por ruta, para que los
 * correos enlacen directo: /reclutador/eventos, /reclutador/ganancias.
 */
export function MyWorkScreen() {
  return (
    <TermsCheck>
      <MyWorkContent />
    </TermsCheck>
  );
}

function MyWorkContent() {
  const { tab: raw } = useParams();
  const navigate = useNavigate();
  const tab: Tab = TABS.includes(raw as Tab) ? (raw as Tab) : "inicio";
  const q = useQuery({ queryKey: ["recruiter-self"], queryFn: recruiterSelfApi.me, refetchOnWindowFocus: true });

  if (q.isLoading) return <LoadingBlock label="Cargando tu espacio…" />;
  if (q.isError || !q.data) {
    const msg = apiError(q.error, "");
    return msg ? <Empty>{msg}</Empty> : <ErrorBlock onRetry={() => q.refetch()} />;
  }
  const w = q.data;
  const todosUrgent = w.todos.filter((t) => t.priority === 0).length;
  const tabs: TabDef<Tab>[] = [
    { id: "inicio", label: "Inicio", count: todosUrgent || undefined },
    { id: "cartera", label: "Cartera" },
    { id: "eventos", label: "Eventos" },
    { id: "ganancias", label: "Ganancias" },
    { id: "datos", label: "Mis datos" },
  ];

  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <p className="text-sm text-fg-muted">Programa de Reclutadores</p>
        <h1 className="nx-h1">Hola, {w.recruiter.name.split(" ")[0]}</h1>
        {w.recruiter.status === "ended" && (
          <p className="text-sm text-warning mt-1">Tu acuerdo terminó el {dayLong(w.recruiter.endedOn)}: puedes ver tus ganancias y subir tus boletas.</p>
        )}
      </div>
      <Tabs tabs={tabs} active={tab} onChange={(t) => navigate(t === "inicio" ? "/reclutador" : `/reclutador/${t}`)} />
      {tab === "inicio" && <HomeTab w={w} />}
      {tab === "cartera" && <PortfolioTab w={w} />}
      {tab === "eventos" && <EventsTab w={w} />}
      {tab === "ganancias" && <EarningsTab w={w} />}
      {tab === "datos" && <DataTab w={w} />}
    </div>
  );
}

// ─── Inicio ─────────────────────────────────────────────────────────────────

const TODO_ICON: Record<Todo["kind"], typeof Check> = {
  precontact: CalendarDays,
  hub_check: CalendarDays,
  presence: CalendarDays,
  report: FileText,
  observed: AlertTriangle,
  expiring: AlertTriangle,
  invoice: Wallet,
};

function HomeTab({ w }: { w: MyWorkspace }) {
  const navigate = useNavigate();
  const e = w.earnings;
  const go = (t: Todo) => navigate(t.kind === "invoice" ? "/reclutador/ganancias" : t.kind === "expiring" ? "/reclutador/cartera" : "/reclutador/eventos");
  const next = w.events.filter((ev) => ev.date >= w.today && !ev.canceled).slice(0, 3);
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3">
        <Stat label="Por cobrar" value={money(e.payable + e.inStatement)} tone="accent" note="Conciliado, va en tu liquidación" />
        <Stat label="En camino" value={money(e.estimated + e.pendingChecklist)} note="Estimado de eventos aún sin conciliar" />
      </div>

      <section className="space-y-2">
        <SectionTitle>Lo que tienes pendiente</SectionTitle>
        {w.todos.length === 0 ? (
          <Empty>Todo al día.</Empty>
        ) : (
          <ul className="grid gap-2">
            {w.todos.map((t, i) => {
              const Icon = TODO_ICON[t.kind];
              return (
                <li key={`${t.kind}-${i}`}>
                  <button type="button" onClick={() => go(t)} className="nx-card-interactive w-full text-left p-3 flex items-start gap-3">
                    <span className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${t.priority === 0 ? "bg-danger-soft text-danger" : "bg-accent-soft text-accent"}`}>
                      <Icon size={15} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-fg">{t.title}</span>
                      <span className="block text-xs text-fg-muted">{t.detail}</span>
                      {t.dueDate && <span className={`block text-xs mt-0.5 ${t.priority === 0 ? "text-danger" : "text-fg-subtle"}`}>Hasta el {dayLong(t.dueDate)} · {relative(t.dueDate, w.today)}</span>}
                    </span>
                    <ChevronRight size={16} className="text-fg-subtle shrink-0 mt-2" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {next.length > 0 && (
        <section className="space-y-2">
          <SectionTitle>Próximos eventos</SectionTitle>
          <ul className="grid gap-2">
            {next.map((ev) => (
              <li key={ev.id} className="nx-card p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-fg truncate">{ev.name}</div>
                  <div className="text-xs text-fg-muted">{ev.producerName} · {dayLong(ev.date)} · {relative(ev.date, w.today)}</div>
                </div>
                <Badge tone={ev.stage === "activation" ? "accent" : "info"}>{STAGE[ev.stage]}</Badge>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// ─── Cartera ────────────────────────────────────────────────────────────────

function PortfolioTab({ w }: { w: MyWorkspace }) {
  const [proposing, setProposing] = useState(false);
  const [activityFor, setActivityFor] = useState<number | null>(null);
  const ended = w.recruiter.status === "ended";
  const groups = [
    { title: "Activos", rows: w.producers.filter((p) => p.status === "active") },
    { title: "En proceso", rows: w.producers.filter((p) => p.status === "pipeline") },
    { title: "Propuestos (Mixcore los está revisando)", rows: w.producers.filter((p) => p.status === "proposed") },
    { title: "Historial", rows: w.producers.filter((p) => ["expired", "rejected", "released"].includes(p.status)) },
  ];
  const t = w.program.terms;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-fg-muted max-w-md">
          Un productor queda a tu nombre desde que Mixcore lo registra en el CRM. Si pasan {t.attributionExpiryDays} días sin
          actividad antes de su primer evento, la asignación caduca.
        </p>
        {!ended && (
          <button type="button" className="nx-btn-primary" onClick={() => setProposing(true)}>
            <Plus size={14} /> Proponer productor
          </button>
        )}
      </div>
      {w.producers.length === 0 && <Empty>Aún no tienes productores. Propón el primero: Mixcore lo registra y queda a tu nombre.</Empty>}
      {groups
        .filter((g) => g.rows.length)
        .map((g) => (
          <section key={g.title} className="space-y-2">
            <h3 className="text-sm font-semibold text-fg-muted">{g.title}</h3>
            <ul className="grid gap-2">
              {g.rows.map((p) => {
                const st = PRODUCER_STATUS[p.status];
                const days = p.attribution?.daysLeft;
                return (
                  <li key={p.id} className="nx-card p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-fg">{p.name}</div>
                        <div className="text-xs text-fg-muted">
                          {p.registeredOn ? `A tu nombre desde el ${dayLong(p.registeredOn)}` : `Propuesto el ${dayLong(p.createdAt.slice(0, 10))}`}
                        </div>
                      </div>
                      <Badge tone={st.tone}>{st.label}</Badge>
                    </div>
                    {p.status === "active" && (
                      <p className="text-sm text-fg">
                        {p.events.counted} evento{p.events.counted === 1 ? "" : "s"} con ventas · {STAGE[p.events.stage]}
                        {p.events.next ? ` · próximo: ${p.events.next.name}, ${dayLong(p.events.next.date)}` : ""}
                      </p>
                    )}
                    {p.status === "pipeline" && days !== null && days !== undefined && (
                      <p className={`text-sm ${days <= 14 ? "text-warning" : "text-fg-muted"}`}>
                        {days <= 0 ? "Caduca hoy" : `Caduca en ${days} día${days === 1 ? "" : "s"}`} si no registras actividad
                        {p.lastActivityOn ? ` · última: ${dayLong(p.lastActivityOn)}` : ""}.
                      </p>
                    )}
                    {p.decisionNote && <p className="text-xs text-fg-muted">Mixcore: {p.decisionNote}</p>}
                    {(p.status === "pipeline" || p.status === "active") && !ended && (
                      <button type="button" className="nx-btn-secondary" onClick={() => setActivityFor(p.id)}>
                        Registrar actividad
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      {proposing && <ProposeModal onClose={() => setProposing(false)} />}
      {activityFor && <ActivityModal producer={w.producers.find((p) => p.id === activityFor)!} onClose={() => setActivityFor(null)} />}
    </div>
  );
}

// ─── Eventos ────────────────────────────────────────────────────────────────

function EventsTab({ w }: { w: MyWorkspace }) {
  const { push } = useToast();
  const qc = useQueryClient();
  const [reportFor, setReportFor] = useState<RecruiterEventRow | null>(null);
  const [fmFor, setFmFor] = useState<{ ev: RecruiterEventRow; item: ChecklistItem } | null>(null);
  const [simFor, setSimFor] = useState<RecruiterEventRow | null>(null);
  const ended = w.recruiter.status === "ended";
  const upd = useMutation({
    mutationFn: (p: { reId: number; key: string; status: "done" | "pending" | "na"; reason?: "not_requested" }) =>
      recruiterSelfApi.updateItem(p.reId, p.key, { status: p.status, reason: p.reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["recruiter-self"] }),
    onError: (err) => push({ kind: "error", message: apiError(err) }),
  });
  const upcoming = w.events.filter((e) => e.date >= w.today);
  const past = w.events.filter((e) => e.date < w.today).reverse();

  const actions = (ev: RecruiterEventRow) => (item: ChecklistItem) => {
    if (ended || ev.canceled || ev.review.status === "approved" || ev.review.status === "retained") return null;
    const busy = upd.isPending;
    if (item.key === "report") {
      if (ev.date > w.today) return null;
      return (
        <button type="button" className={item.status === "done" ? "nx-btn-ghost" : "nx-btn-primary"} onClick={() => setReportFor(ev)}>
          <FileText size={14} /> {item.status === "done" ? "Editar reporte" : "Enviar reporte"}
        </button>
      );
    }
    if (item.status === "done") {
      return (
        <button type="button" className="nx-btn-ghost" disabled={busy} onClick={() => upd.mutate({ reId: ev.id, key: item.key, status: "pending" })}>
          <Undo2 size={14} /> Deshacer
        </button>
      );
    }
    if (item.status === "na") {
      if (item.naReason === "not_requested") {
        return (
          <button type="button" className="nx-btn-ghost" disabled={busy} onClick={() => upd.mutate({ reId: ev.id, key: item.key, status: "pending" })}>
            El productor la pidió
          </button>
        );
      }
      if (item.naReason === "force_majeure") {
        return (
          <button type="button" className="nx-btn-ghost" disabled={busy} onClick={() => upd.mutate({ reId: ev.id, key: item.key, status: "pending" })}>
            <Undo2 size={14} /> Deshacer
          </button>
        );
      }
      return null;
    }
    const presenceTooEarly = item.key === "presence" && ev.date > w.today;
    return (
      <>
        {!presenceTooEarly && (
          <button type="button" className="nx-btn-primary" disabled={busy} onClick={() => upd.mutate({ reId: ev.id, key: item.key, status: "done" })}>
            <Check size={14} /> Listo
          </button>
        )}
        {item.onlyIfRequested ? (
          <button type="button" className="nx-btn-ghost" disabled={busy} onClick={() => upd.mutate({ reId: ev.id, key: item.key, status: "na", reason: "not_requested" })}>
            No la pidió
          </button>
        ) : (
          <button type="button" className="nx-btn-ghost" onClick={() => setFmFor({ ev, item })}>
            <CircleSlash size={14} /> Fuerza mayor
          </button>
        )}
      </>
    );
  };

  const footer = (ev: RecruiterEventRow) => {
    if (ended || ev.canceled || ev.stage !== "activation" || ev.date < w.today || ev.simultaneous) return null;
    const others = w.events.filter((o) => o.id !== ev.id && o.date === ev.date && o.stage === "activation" && !o.canceled);
    if (others.length === 0) return null;
    return (
      <div className="rounded-md bg-bg-subtle p-3 text-sm flex flex-wrap items-center justify-between gap-2">
        <span className="text-fg-muted">Tienes otro evento de activación esa misma noche.</span>
        <button type="button" className="nx-btn-secondary" onClick={() => setSimFor(ev)}>
          Avisar evento simultáneo
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {w.events.length === 0 && <Empty>Cuando tus productores creen eventos, aparecen acá con su checklist.</Empty>}
      {upcoming.length > 0 && (
        <section className="space-y-2">
          <SectionTitle>Próximos</SectionTitle>
          {upcoming.map((ev) => (
            <EventCard key={ev.id} ev={ev} today={w.today} renderItemActions={actions(ev)} footer={footer(ev)} defaultOpen={ev.date === w.today || ev.progress.overdue > 0} />
          ))}
        </section>
      )}
      {past.length > 0 && (
        <section className="space-y-2">
          <SectionTitle>Realizados</SectionTitle>
          {past.map((ev) => (
            <EventCard key={ev.id} ev={ev} today={w.today} renderItemActions={actions(ev)} defaultOpen={ev.review.status === "observed" || (ev.progress.pending > 0 && !ev.canceled)} />
          ))}
        </section>
      )}
      {reportFor && <ReportModal ev={reportFor} onClose={() => setReportFor(null)} />}
      {fmFor && <ForceMajeureModal ev={fmFor.ev} item={fmFor.item} onClose={() => setFmFor(null)} />}
      {simFor && <SimultaneousModal ev={simFor} candidates={w.events.filter((o) => o.id !== simFor.id && o.date === simFor.date && o.stage === "activation" && !o.canceled)} onClose={() => setSimFor(null)} />}
    </div>
  );
}

// ─── Ganancias ──────────────────────────────────────────────────────────────

function EarningsTab({ w }: { w: MyWorkspace }) {
  const [open, setOpen] = useState<number | null>(null);
  const [invoiceFor, setInvoiceFor] = useState<number | null>(null);
  const e = w.earnings;
  const t = w.program.terms;
  const lines = useMemo(() => [...w.commissions].filter((c) => c.status !== "void").reverse(), [w.commissions]);
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Estimado" value={money(e.estimated)} note="Eventos aún sin conciliar" />
        <Stat label="Por checklist" value={money(e.pendingChecklist)} note="Conciliado, falta aprobar" />
        <Stat label="Por cobrar" value={money(e.payable + e.inStatement)} tone="accent" note="En tu liquidación" />
        <Stat label="Pagado" value={money(e.paid)} tone="success" note="Bruto, antes de la retención" />
      </div>
      {e.retained > 0 && <p className="text-sm text-danger">Retenido: {money(e.retained)} (checklist no subsanado a tiempo).</p>}

      <section className="space-y-2">
        <SectionTitle>Liquidaciones mensuales</SectionTitle>
        <p className="text-xs text-fg-muted">
          Una boleta de honorarios al mes por el monto bruto, a nombre de {w.program.company.name}. La retención ({pct(t.retentionRate)}) la
          declara y entera {w.program.company.name}; tú no la aplicas.
        </p>
        {w.statements.length === 0 ? (
          <Empty>Aún no tienes liquidaciones. Se generan a comienzos de cada mes con lo conciliado del mes anterior.</Empty>
        ) : (
          <ul className="grid gap-2">
            {w.statements.map((s) => {
              const st = STATEMENT_STATUS[s.status];
              return (
                <li key={s.id} className="nx-card p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium text-fg capitalize">{s.periodLabel}</div>
                      <Badge tone={st.tone}>{st.label}</Badge>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-fg-muted">Líquido</div>
                      <div className="text-lg font-bold tabular-nums text-fg">{money(s.net)}</div>
                    </div>
                  </div>
                  <div className="text-sm text-fg-muted">
                    Bruto {money(s.gross)} − retención {money(s.retention)}
                  </div>
                  {s.status === "awaiting_invoice" && (
                    <div className="rounded-md bg-accent-soft p-3 space-y-2">
                      {s.invoiceNote && <p className="text-sm text-warning">Mixcore observó tu boleta: {s.invoiceNote}</p>}
                      <p className="text-sm text-fg">
                        Emite tu boleta por <b>{money(s.gross)}</b> a {w.program.company.name}, RUT {w.program.company.rut}.
                      </p>
                      <button type="button" className="nx-btn-primary" onClick={() => setInvoiceFor(s.id)}>
                        <FileText size={14} /> Subir boleta
                      </button>
                    </div>
                  )}
                  {s.status === "invoice_submitted" && <p className="text-sm text-fg-muted">Recibimos tu boleta n.º {s.invoiceNumber}: te pagamos en los próximos días.</p>}
                  {s.status === "paid" && (
                    <p className="text-sm text-success">
                      Pagada el {dayLong(s.paidAt?.slice(0, 10))}
                      {s.receiptUrl && (
                        <>
                          {" · "}
                          <a href={s.receiptUrl} target="_blank" rel="noreferrer" className="underline">
                            ver comprobante
                          </a>
                        </>
                      )}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="space-y-2">
        <SectionTitle>Por evento</SectionTitle>
        {lines.length === 0 ? (
          <Empty>Tus comisiones aparecen cuando un productor de tu cartera vende su primer evento.</Empty>
        ) : (
          <ul className="grid gap-2">
            {lines.map((c) => (
              <li key={c.id} className="nx-card">
                <button type="button" className="w-full text-left p-4 flex items-center justify-between gap-3" onClick={() => setOpen(open === c.id ? null : c.id)}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-fg truncate">{lineTitle(c)}</div>
                    <div className="text-xs text-fg-muted truncate">{c.producerName ?? ""}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className="text-sm font-semibold tabular-nums text-fg">{money(c.amount)}</span>
                    <CommissionStatusBadge status={c.status} />
                  </div>
                </button>
                {open === c.id && (
                  <div className="px-4 pb-4">
                    <CommissionBreakdownView line={c} />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
      {invoiceFor && <InvoiceModal statement={w.statements.find((s) => s.id === invoiceFor)!} company={w.program.company} onClose={() => setInvoiceFor(null)} />}
    </div>
  );
}

// ─── Mis datos ──────────────────────────────────────────────────────────────

function DataTab({ w }: { w: MyWorkspace }) {
  const r = w.recruiter;
  const t = w.program.terms;
  return (
    <div className="space-y-5">
      <section className="nx-card p-4 space-y-3">
        <SectionTitle>Datos para pagarte</SectionTitle>
        <PaymentForm recruiter={r} />
      </section>
      <section className="nx-card p-4 space-y-3">
        <SectionTitle>Tu acuerdo</SectionTitle>
        <KeyValue
          rows={[
            ["Acuerdo", `${r.agreementVersion || w.program.agreementVersion}${r.agreementSignedOn ? ` · firmado el ${dayLong(r.agreementSignedOn)}` : ""}`],
            ["Fee de originación", `${money(t.originationFee)} por productor, cuando vende su primer evento`],
            [`Eventos 1 al ${t.activationEvents}`, `${pct(t.activationRate)} de lo que gana Mixcore`],
            [`Desde el evento ${t.activationEvents + 1}`, `${pct(t.maintenanceRate)} de lo que gana Mixcore`],
            ["Base", "La comisión de servicio que recibe Mixcore por el evento, neta de IVA y de reembolsos"],
            ["Pago", `Una boleta mensual a ${w.program.company.name}; retención ${pct(t.retentionRate)}`],
          ]}
        />
        <p className="text-xs text-fg-muted">
          <a className="text-accent underline" href="https://mixcore.club/terminos/colaboradores" target="_blank" rel="noreferrer">
            Términos y Condiciones para Colaboradores
          </a>{" "}
          ·{" "}
          <a className="text-accent underline" href="https://mixcore.club/privacidad" target="_blank" rel="noreferrer">
            Política de Privacidad
          </a>
        </p>
      </section>
      <section className="nx-card p-4 space-y-3">
        <SectionTitle>Checklist por evento · versión {w.program.checklist.version}</SectionTitle>
        <div className="grid gap-4 sm:grid-cols-2">
          {(["activation", "maintenance"] as const).map((stage) => (
            <div key={stage}>
              <div className="text-sm font-medium text-fg mb-1">{stage === "activation" ? `Activación · eventos 1 al ${t.activationEvents}` : `Mantención · desde el ${t.activationEvents + 1}`}</div>
              <ol className="list-decimal pl-5 space-y-1 text-sm text-fg-muted">
                {w.program.checklist[stage].map((it) => (
                  <li key={it.key}>
                    <span className="text-fg">{it.label}:</span> {it.help}
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
        <p className="text-xs text-fg-subtle">
          Si Mixcore observa un ítem, tienes {t.cureBusinessDays} días hábiles para subsanarlo; la comisión sólo se retiene si no se
          subsana. Enfermedad, emergencia o cancelación del productor no cuentan como incumplimiento.
        </p>
      </section>
    </div>
  );
}
